import { DOCUMENT } from '@angular/common';
import { effect, inject, Injectable, Signal } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

import { OpenGraph } from '../model/open-graph';
import { StorefrontPage } from '../model/storefront-page';
import { StorefrontContext } from './storefront-context';
import { WebsiteService } from './website.service';

/** What a page component hands the head: its resolved data, plus Open Graph if it has any. */
type PageData = StorefrontPage & { openGraph?: OpenGraph };

/**
 * The head, as the `fashe/head` partial built it: title with its optional " | shop" suffix, the
 * description that falls back to the title, keywords, canonical, robots, favicon, JSON-LD and
 * Open Graph. Keeping it in one place is what lets the page components stay markup-only.
 */
@Injectable({ providedIn: 'root' })
export class SeoService {

  private document = inject(DOCUMENT);
  private title = inject(Title);
  private meta = inject(Meta);
  private websiteService = inject(WebsiteService);
  private context = inject(StorefrontContext);
  private properties: string[] = [];

  /**
   * Keeps the head in step with a page's resolved data. Called from a page component's
   * constructor, so the effect lives exactly as long as the page does and re-runs when the router
   * resolves the route again - which, with a query-string filter, is on every navigation.
   */
  page(data: Signal<PageData>, titleSuffix = false): void {
    effect(() => {
      const resolved = data();
      this.apply(resolved, titleSuffix);
      this.openGraph(resolved.openGraph);
    });
  }

  apply(page: StorefrontPage, titleSuffix = false): void {
    const website = this.websiteService.current();
    const pageTitle = (page.title ?? '').trim();
    this.title.setTitle(titleSuffix ? `${pageTitle} | ${website.name}` : pageTitle);
    this.meta.updateTag({
      name: 'description',
      content: page.description?.trim() ?? `${pageTitle}. Інтернет-магазин ${website.name}.`
    });
    this.meta.updateTag({ name: 'keywords', content: `${website.name}, ${pageTitle}` });
    this.link('icon', website.icon, 'image/png');
    this.canonical(page.canonical);
    this.robots(page.robots);
    this.jsonLd(page.jsonLd);
  }

  /**
   * Only the product page has Open Graph tags, so navigating away has to take them off again -
   * including the `product:price:*` ones, which is why the tags written last time are remembered
   * rather than matched by an `og:` prefix.
   */
  openGraph(openGraph: OpenGraph | undefined): void {
    this.properties.forEach(property => this.meta.removeTag(`property="${property}"`));
    this.properties = [];
    if (!openGraph) {
      return;
    }
    const website = this.websiteService.current();
    this.property('og:site_name', website.name);
    this.property('og:locale', this.context.locale());
    this.property('og:title', openGraph.title);
    this.property('og:url', openGraph.url);
    this.property('og:type', openGraph.type);
    this.property('og:image', openGraph.image);
    this.property('og:description', openGraph.description);
    openGraph.additionalProperties.forEach(p => this.property(p.property, p.content));
  }

  /** The `/css/dynamic.css` endpoint of the Mustache app: the shop's palette as CSS variables. */
  colors(): void {
    const { dark, light, lightActive } = this.websiteService.current().colors ?? {};
    const css = `:root{--color-dark:${dark};--color-light:${light};--color-light-active:${lightActive};}`;
    const existing = this.document.getElementById('dynamic-colors');
    if (existing) {
      existing.textContent = css;
      return;
    }
    const style = this.document.createElement('style');
    style.id = 'dynamic-colors';
    style.textContent = css;
    this.document.head.appendChild(style);
  }

  private canonical(url: string | undefined): void {
    this.link('canonical', url);
  }

  private robots(content: string | undefined): void {
    if (content) {
      this.meta.updateTag({ name: 'robots', content });
    } else {
      this.meta.removeTag('name="robots"');
    }
  }

  private jsonLd(json: string | undefined): void {
    const existing = this.document.getElementById('json-ld');
    if (existing) {
      existing.remove();
    }
    if (!json) {
      return;
    }
    const script = this.document.createElement('script');
    script.id = 'json-ld';
    script.type = 'application/ld+json';
    script.textContent = json;
    this.document.head.appendChild(script);
  }

  private link(rel: string, href: string | undefined, type?: string): void {
    const existing = this.document.head.querySelector(`link[rel="${rel}"]`);
    if (!href) {
      existing?.remove();
      return;
    }
    const link = existing ?? this.document.createElement('link');
    link.setAttribute('rel', rel);
    link.setAttribute('href', href);
    if (type) {
      link.setAttribute('type', type);
    }
    if (!existing) {
      this.document.head.appendChild(link);
    }
  }

  private property(property: string, content: string | undefined): void {
    if (content) {
      this.meta.updateTag({ property, content }, `property="${property}"`);
      this.properties.push(property);
    }
  }

}
